import * as Ariakit from '@ariakit/react'
import classnames from 'classnames'
import * as d3 from 'd3'
import { isRedesignEnabled } from 'finviz-charts/app/utils'
import * as React from 'react'
import { Component, RefObject, createRef } from 'react'

import { ChipSwitch, ChipSwitchButton } from '../../main/components/ChipSwitch'
import { RedGreenText } from '../../main/components/RedGreenText'
import { FinancialsTableContextProvider, useFinancialsTableContext } from '../../main/components/financials-table'
import { Icon } from '../../main/components/icon'
import { getValue, setValue } from '../../main/services/local-storage'
import { shortFormatNumber } from '../../main/util'
import { decodeQueryString } from '../queryString'
import { AdTag, getInvestingChannelPage } from '../shared/components/Ads'
import BarChartBig from './barChart'
import {
  DEFAULT_SETTINGS,
  SETTINGS_COOKIE_KEY,
  StatementsDataEnum,
  StatementsTimeframeEnum,
  StatementsType,
} from './statements-constants'

interface Bar {
  name: string
  value: number
}

interface BarChartProps {
  data: Bar[]
}

class BarChart extends Component<BarChartProps> {
  svgRef: RefObject<SVGSVGElement>

  constructor(props: BarChartProps) {
    super(props)
    this.svgRef = createRef()
  }

  componentDidMount() {
    this.renderChart()
  }

  componentDidUpdate() {
    this.renderChart()
  }

  renderChart() {
    const data = this.props.data

    const barWidth = 3
    const width = data.length * (barWidth + 1)
    const height = 16

    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, width])

    let min = d3.min(data, (d: Bar) => d.value)!
    if (min < 0) {
      min = d3.min(data, (d: Bar) => Math.min(d.value, -d.value))!
    } else {
      min = 0
    }

    const y = d3
      .scaleLinear()
      .range([height, 0])
      .domain([min, d3.max(data, (d: Bar) => Math.abs(d.value))] as [number, number])
      .nice()
      .unknown(height / 2)

    const svg = d3.select(this.svgRef.current).property('value', [])

    let bars = svg.selectAll('.bar').data(data)
    bars
      .enter()
      .append('rect')
      .attr('class', function (d: Bar) {
        return 'bar bar--' + (d.value < 0 ? 'negative' : 'positive')
      })
      .attr('x', function (d: Bar) {
        return x(d.name)!
      })
      .attr('y', function (d: Bar) {
        return y(Math.max(0, d.value))
      })
      .attr('height', function (d: Bar) {
        let height = Math.abs(y(d.value) - y(0))
        if (d.value > 0 || d.value < 0) {
          height = Math.max(height, 0.5)
        }
        return height
      })
      .attr('width', barWidth)
    bars
      .transition()
      .attr('class', function (d: Bar) {
        return 'bar bar--' + (d.value < 0 ? 'negative' : 'positive')
      })
      .attr('x', function (d: Bar) {
        return x(d.name)!
      })
      .attr('y', function (d: Bar) {
        return y(Math.max(0, d.value))
      })
      .attr('height', function (d: Bar) {
        let height = Math.abs(y(d.value) - y(0))
        if (d.value > 0 || d.value < 0) {
          height = Math.max(height, 0.5)
        }
        return height
      })
      .attr('width', barWidth)
    bars.exit().remove()

    this.svgRef.current!.style.width = width + 'px'
    this.svgRef.current!.style.height = height + 'px'
  }

  render() {
    return <svg ref={this.svgRef} className="quote-bar-chart"></svg>
  }
}

function EliteHoverAd() {
  return (
    <div className="quote-statements-elite-tooltip">
      <div className="quote-statements-elite-tooltip_wrapper">
        <Icon name="lock" width="24" className="mx-auto text-blue-500" />
        <h2 className="quote-statements-elite-subscribe_title">Upgrade your FINVIZ experience</h2>
        <p>
          Join thousands of traders who make more informed decisions with&nbsp;our&nbsp;premium features. Real-time
          quotes, advanced&nbsp;visualizations, historical statements, and much more.
        </p>
        <a
          href="/elite.ashx?utm_source=finviz&utm_medium=banner&utm_campaign=quote-statements"
          target="_blank"
          className="tab-link"
        >
          Learn more
        </a>
      </div>
    </div>
  )
}

interface BarChartWrapperProps {
  data: Bar[]
}

class BarChartWrapper extends Component<BarChartWrapperProps> {
  state = {
    hover: false,
  }

  render() {
    return (
      <div className="tooltip">
        <Ariakit.TooltipProvider placement="right" showTimeout={0}>
          <Ariakit.TooltipAnchor disabled={!this.props.data.some((x) => x.value !== null)}>
            <BarChart data={this.props.data} />
          </Ariakit.TooltipAnchor>
          <Ariakit.Tooltip
            portal={false}
            className={classnames('statements-table_tooltiptext quote_statements-tooltip z-10', {
              'is-ad': !FinvizSettings.hasUserPremium,
            })}
          >
            {FinvizSettings.hasUserPremium ? (
              <BarChartBig data={this.props.data} xAxisRotate={true} />
            ) : (
              <EliteHoverAd />
            )}
          </Ariakit.Tooltip>
        </Ariakit.TooltipProvider>
      </div>
    )
  }
}

const DEFAULT_SOURCE = 'F'

const _getDefaultSource = (): 'F' | 'R' => {
  if (!FinvizSettings.hasUserPremium) {
    return DEFAULT_SOURCE
  }

  return getValue<string>('statements-source', DEFAULT_SOURCE) === 'R' ? 'R' : 'F'
}

const getTimeframeFromStatementsSettingsTimeframe = (statementsTimeframe: StatementsTimeframeEnum) => {
  if (statementsTimeframe === StatementsTimeframeEnum.quarterly) {
    return 'Q'
  }

  // statementsTimeframe === StatementsTimeframeEnum.annual or fallback
  return 'A'
}

interface StatementsState {
  source: 'F' | 'R'
  sheet: string
  currency: string | null
  data: Record<string, string[]> | null
}

function StatementsComponent() {
  const { settings } = useFinancialsTableContext<StatementsType>()
  const pageRef = React.useRef(getInvestingChannelPage())

  const [state, setState] = React.useState<StatementsState>({
    source: _getDefaultSource(),
    sheet: 'I',
    currency: null,
    data: null,
  })

  const timeframe = getTimeframeFromStatementsSettingsTimeframe(settings.statementsTimeframe)

  const { data } = state
  const hasData = data && !data.error && Object.keys(data).length > 0
  const hasRedesignEnabled = FinvizSettings?.hasRedesignEnabled

  const _fetch = async () => {
    const { source, sheet } = state
    const { t } = decodeQueryString()

    try {
      const response = await fetch(`/api/statement.ashx?t=${t}&so=${source}&s=${sheet}${timeframe}`)
      const json = await response.json()

      setState({
        ...state,
        currency: json.currency,
        data: json.error ? json : json.data,
      })
    } catch {}
  }

  React.useEffect(() => {
    pageRef.current = getInvestingChannelPage()
    pageRef.current?.create()

    return () => {
      pageRef.current?.destroy()
    }
  }, [])

  React.useEffect(() => {
    _fetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sheet, state.source, timeframe])

  const _renderSheetLink = (id: string, text: string) => (
    <a
      href="#"
      className={classnames('tab-link', { 'font-bold': state.sheet === id })}
      onClick={(e) => {
        e.preventDefault()
        setState({
          ...state,
          sheet: id,
        })
      }}
    >
      {text}
    </a>
  )

  if (data === null) {
    return null
  }

  return (
    <>
      <table width="100%" cellPadding="3" className="statements-table-header">
        <tbody>
          <tr>
            <td style={FinvizSettings.hasUserPremium ? { paddingLeft: 0 } : {}}>
              {FinvizSettings.hasUserPremium && (
                // TODO fix classname when css mixins removal is merged
                <select
                  className={classnames({
                    'myaccount-edit w-auto': !hasRedesignEnabled,
                    'fv-select': hasRedesignEnabled,
                  })}
                  value={state.source}
                  onChange={(e) => {
                    e.preventDefault()
                    const source = e.target.value === 'R' ? 'R' : 'F'
                    setState({
                      ...state,
                      source: source,
                    })
                    setValue('statements-source', source, DEFAULT_SOURCE, false)
                  }}
                >
                  <option value="F">FactSet</option>
                  <option value="R">Reuters</option>
                </select>
              )}
              {FinvizSettings.hasUserPremium && <>&nbsp;&nbsp;&nbsp;</>}

              <span className="fullview-links">
                {_renderSheetLink('I', 'Income Statement')} <span className="text-muted-3">•</span>{' '}
                {_renderSheetLink('B', 'Balance Sheet')} <span className="text-muted-3">•</span>{' '}
                {_renderSheetLink('C', 'Cash Flow')}
              </span>
            </td>
            <td align="right">
              <StatementsSettingsSection />
            </td>
          </tr>
        </tbody>
      </table>
      {hasData && <StatementsTable data={state.data} currency={state.currency} />}
      {!hasData && (
        <table
          width="100%"
          cellPadding="3"
          data-testid="quote-statements-table"
          className={classnames({
            'styled-table-new is-rounded quote_statements-table is-tabular-nums': hasRedesignEnabled,
            'snapshot-table2 has-hovers statements-table': !hasRedesignEnabled,
            'is-free': !FinvizSettings.hasUserPremium,
          })}
        >
          <tbody>
            <tr className="styled-row is-hoverable is-bordered is-rounded is-border-top is-hover-borders">
              <td className={hasRedesignEnabled ? '' : 'snapshot-td2'}>
                The statement is not available. Try switching between FactSet-Reuters source.
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </>
  )
}

function Statements() {
  return (
    <FinancialsTableContextProvider defaultSettings={DEFAULT_SETTINGS} cookieKey={SETTINGS_COOKIE_KEY}>
      <StatementsComponent />
    </FinancialsTableContextProvider>
  )
}

function StatementsSettingsSection() {
  const { settings, setSettings } = useFinancialsTableContext<StatementsType>()

  const toggleSetting = (key: keyof StatementsType, value: StatementsType[keyof StatementsType]) => {
    setSettings({
      ...settings,
      [key]: value,
    })
  }

  const yoyGrowthLabel = React.useMemo(() => {
    const isQuarterlyTimeframe = settings.statementsTimeframe === StatementsTimeframeEnum.quarterly

    if (isQuarterlyTimeframe) {
      return 'QoQ Growth'
    }

    return 'YoY Growth'
  }, [settings.statementsTimeframe])

  return (
    <div className="flex flex-row justify-end space-x-5">
      <ChipSwitch size="small" label="Data" rounding={!isRedesignEnabled() ? 'none' : undefined}>
        <ChipSwitchButton
          active={settings[StatementsDataEnum.yoyChange]}
          onClick={() => {
            toggleSetting(StatementsDataEnum.yoyChange, !settings[StatementsDataEnum.yoyChange])
          }}
        >
          {yoyGrowthLabel}
        </ChipSwitchButton>
        <ChipSwitchButton
          active={settings[StatementsDataEnum.yoyChangePct]}
          onClick={() => {
            toggleSetting(StatementsDataEnum.yoyChangePct, !settings[StatementsDataEnum.yoyChangePct])
          }}
        >
          {`${yoyGrowthLabel} %`}
        </ChipSwitchButton>
      </ChipSwitch>

      <ChipSwitch size="small" label="Timeframe" rounding={!isRedesignEnabled() ? 'none' : undefined}>
        <ChipSwitchButton
          active={settings.statementsTimeframe === StatementsTimeframeEnum.annual}
          onClick={() => {
            toggleSetting('statementsTimeframe', StatementsTimeframeEnum.annual)
          }}
        >
          Annual
        </ChipSwitchButton>
        <ChipSwitchButton
          active={settings.statementsTimeframe === StatementsTimeframeEnum.quarterly}
          onClick={() => {
            toggleSetting('statementsTimeframe', StatementsTimeframeEnum.quarterly)
          }}
        >
          Quarterly
        </ChipSwitchButton>
      </ChipSwitch>
    </div>
  )
}

function isChartable(label: string) {
  switch (label) {
    case 'Period End Date':
    case 'Period Length':
      return false
  }

  return true
}

function parseData(data: Record<string, string[]>, skipFirst: boolean) {
  let parsed: any = {}
  Object.keys(data).forEach((key) => {
    parsed[key] = data[key]
      .map((value, index) => {
        if (isChartable(key)) {
          let parsedValue
          if (value === '') {
            parsedValue = null
          } else {
            parsedValue = parseFloat(value.replace(/,/g, ''))
          }
          return {
            name: data['Period End Date'][index],
            value: parsedValue,
          }
        }
        return null
      })
      .slice(skipFirst ? 1 : 0)
  })

  return parsed as Record<string, Array<{ name: string; value: number }>>
}

function isHighlightRow(label: string) {
  switch (label) {
    case 'Period End Date':
    case 'Total Revenue':
    case 'Total Premiums Earned':
    case 'Interest Income':
    case 'Gross Profit':
    case 'Total Operating Expense':
    case 'Operating Income':
    case 'Net Income Before Taxes':
    case 'Net Income':
    case 'Total Current Assets':
    case 'Total Assets':
    case 'Total Current Liabilities':
    case 'Total Liabilities':
    case 'Total Equity':
    case 'Total Liabilities and Equity':
    case 'Cash from Operating Activities':
    case 'Cash from Investing Activities':
    case 'Cash from Financing Activities':
    case 'Net Change in Cash':
    case 'Price To Earnings Ratio':
    case 'Price to Book Ratio':
    case 'Price to Free Cash Flow':
      return true
  }

  return false
}

type StatementsTableProps = Pick<StatementsState, 'data' | 'currency'>

function StatementsTable({ data, currency }: StatementsTableProps) {
  const { settings } = useFinancialsTableContext<StatementsType>()
  const skipFirst = data?.['Period End Date'][0] === 'TTM'
  const parsedData = data ? parseData(data, skipFirst) : null

  const yoyData = React.useMemo(
    () =>
      parsedData
        ? Object.entries(parsedData).reduce<
            Record<
              string,
              {
                change: Array<number | null>
                changePct: Array<number | null>
              }
            >
          >((acc, [key, values]) => {
            if (!isChartable(key)) {
              return acc
            }
            const yoyData: Array<{ change: number | null; changePct: number | null }> = []
            let lastValue: number | null = null

            // values are in DESC order
            const reversedValues = [...values].reverse()

            reversedValues.forEach(({ value }) => {
              const isValidValue = typeof value === 'number'
              if (!isValidValue || lastValue === null || lastValue === 0) {
                yoyData.push({ change: null, changePct: null })
              } else {
                const change = value - lastValue
                yoyData.push({ change, changePct: (change / Math.abs(lastValue)) * 100 })
              }

              if (isValidValue) lastValue = value
            })

            const reversedYoyData = [...yoyData].reverse()
            return {
              ...acc,
              [key]: {
                change: reversedYoyData.map(({ change }) => change),
                changePct: reversedYoyData.map(({ changePct }) => changePct),
              },
            }
          }, {})
        : {},
    [parsedData]
  )

  const yoyGrowthLabel = React.useMemo(() => {
    const isQuarterlyTimeframe = settings.statementsTimeframe === StatementsTimeframeEnum.quarterly

    if (isQuarterlyTimeframe) {
      return 'QoQ Growth'
    }

    return 'YoY Growth'
  }, [settings.statementsTimeframe])

  if (!data || !parsedData) return null

  const hasRedesignEnabled = FinvizSettings?.hasRedesignEnabled
  const cellClass = classnames('align-top', {
    'snapshot-td2 h-6 !leading-4 !pt-[4px]': !hasRedesignEnabled,
  })
  const adRowIndexStart = !hasRedesignEnabled && data['Period Length'] ? 2 : 1

  return (
    <>
      <table
        width="100%"
        cellPadding="3"
        cellSpacing="0"
        data-testid="quote-statements-table"
        className={classnames({
          'styled-table-new is-rounded quote_statements-table is-tabular-nums': hasRedesignEnabled,
          'snapshot-table2 has-hovers statements-table': !hasRedesignEnabled,
          'is-free': !FinvizSettings.hasUserPremium,
        })}
      >
        <tbody>
          {Object.keys(data).map((key, rowIndex) => {
            const hasYoYData = !!yoyData[key] && data[key].some((value) => value)
            return (
              <tr
                key={key}
                className={classnames({
                  'styled-row is-hoverable is-bordered is-rounded is-border-top is-hover-borders': hasRedesignEnabled,
                  'is-highlighted': hasRedesignEnabled && isHighlightRow(key),
                  'table-dark-row': !hasRedesignEnabled && isHighlightRow(key),
                  'table-light-row': !hasRedesignEnabled && !isHighlightRow(key),
                  'first-row': rowIndex === 0,
                  'elite-row': rowIndex === adRowIndexStart,
                })}
              >
                <td className={cellClass}>
                  {key}
                  {hasYoYData && settings[StatementsDataEnum.yoyChange] && (
                    <>
                      <br />
                      <span className="text-muted-2">{yoyGrowthLabel}</span>
                    </>
                  )}
                  {hasYoYData && settings[StatementsDataEnum.yoyChangePct] && (
                    <>
                      <br />
                      <span className="text-muted-2">{`${yoyGrowthLabel} %`}</span>
                    </>
                  )}
                </td>
                <td className={cellClass} align="center">
                  {isChartable(key) && <BarChartWrapper data={parsedData[key].reverse()} />}
                </td>
                {data[key].map((value, index) => {
                  const changeIndex = skipFirst ? index - 1 : index
                  const yoyChangeValue = yoyData[key]?.change[changeIndex] ?? null
                  const yoyChangePctValue = yoyData[key]?.changePct[changeIndex] ?? null
                  if (!FinvizSettings.hasUserPremium) {
                    if (rowIndex === adRowIndexStart && index === 3) {
                      return (
                        <td
                          key={index}
                          align="right"
                          className="quote-statements-elite-subscribe align-middle"
                          rowSpan={Object.keys(data).length - 1}
                          colSpan={data[key].length - 3}
                        >
                          <AdTag
                            name="IC_Statements"
                            className="mx-auto -mt-32 mb-15 text-center text-2xs text-blue-500"
                          />
                          <Icon name="lock" width="24" className="mx-auto text-blue-500" />
                          <h2 className="quote-statements-elite-subscribe_title">Upgrade your FINVIZ experience</h2>
                          <p className="quote-statements-elite-subscribe_text">
                            Join thousands of traders who make more informed decisions with&nbsp;our&nbsp;premium
                            features. Real-time quotes, advanced&nbsp;visualizations, historical statements, and much
                            more.
                          </p>
                          <a
                            href="/elite.ashx?utm_source=finviz&utm_medium=banner&utm_campaign=quote-statements"
                            target="_blank"
                            className="tab-link"
                          >
                            Learn more
                          </a>
                        </td>
                      )
                    }
                    if (
                      index > 2 &&
                      ((hasRedesignEnabled && key !== 'Period End Date') ||
                        (key !== 'Period End Date' && key !== 'Period Length'))
                    ) {
                      return null
                    }
                  }

                  return (
                    <td key={index} align="right" className={cellClass}>
                      {value}
                      {hasYoYData && settings[StatementsDataEnum.yoyChange] && (
                        <>
                          <br />
                          <RedGreenText value={yoyChangeValue} rightContent={null}>
                            {shortFormatNumber(yoyChangeValue, { defaultValue: '—' })}
                          </RedGreenText>
                        </>
                      )}
                      {hasYoYData && settings[StatementsDataEnum.yoyChangePct] && (
                        <>
                          <br />
                          <RedGreenText
                            value={yoyChangePctValue}
                            rightContent={yoyChangePctValue !== null ? '%' : null}
                          >
                            {yoyChangePctValue?.toFixed(2) ?? '—'}
                          </RedGreenText>
                        </>
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      <table className="quote_statements-footer" width="100%" cellPadding="3">
        <tbody>
          <tr>
            <td align="right">values in Millions, {currency}</td>
          </tr>
        </tbody>
      </table>
    </>
  )
}

export default Statements
